import type { ReactNode } from 'react';
import { match, P } from 'ts-pattern';
import { AssetFormat, type AssetFormatProps } from '../../../format/asset';
import { CurrencyFormat, type CurrencyFormatProps } from '../../../format/currency';
import { DateFormat, type DateFormatProps } from '../../../format/date';
import { ID, type IDProps } from '../../../format/id';
import { NumberFormat, type NumberFormatProps } from '../../../format/number';
import { SensitiveValueFormat, type SensitiveValueFormatProps } from '../../../format/sensitive-value';
import type { LinkElementType } from '../../../link/base-link';
import { TextLink, type TextLinkProps } from '../../../link/text-link/text-link';
import { Skeleton, type SkeletonProps } from '../../../loading/skeleton';
import { Typography, type TypographyProps } from '../../../typography';
import { TypographyProvider } from '../../../typography/context';

interface EmptyStateRenderer {
  renderWhenEmpty?: ReactNode;
  renderWhenPending?: SkeletonProps;
}

export type ReadonlyListItemValueType =
  | {
      string: { value: string | undefined } & EmptyStateRenderer & Partial<Omit<TextLinkProps, 'children'>>;
    }
  | ({ sensitiveString: SensitiveValueFormatProps } & EmptyStateRenderer)
  | ({ number: NumberFormatProps } & EmptyStateRenderer)
  | ({ currency: CurrencyFormatProps } & EmptyStateRenderer)
  | ({ asset: AssetFormatProps } & EmptyStateRenderer)
  | ({ date: DateFormatProps } & EmptyStateRenderer)
  | ({ id: IDProps } & EmptyStateRenderer)
  | ({ yesOrNo: { value: boolean | undefined; trueLabel?: string; falseLabel?: string } } & EmptyStateRenderer)
  | ({ node: { value: ReactNode } } & EmptyStateRenderer);

export const defaultValueTypographyProps = {
  tone: 'neutral-dk',
  size: '0.875rem',
  lineHeight: '1.125rem',
  align: 'right',
  wrap: 'preserve',
  variant: 'main',
  inheritance: 'current',
} as const satisfies TypographyProps;

const defaultTextLinkValueTypographyProps = {
  ...defaultValueTypographyProps,
  tone: 'md',
} as const satisfies TypographyProps;

export const defaultValueStackedTypographyProps = {
  ...defaultValueTypographyProps,
  align: 'left',
  inheritance: 'current',
} as const satisfies TypographyProps;

export const DEFAULT_EMPTY_VALUE = '--';

const defaultPendingProps: SkeletonProps = {
  charCount: 20,
  type: 'line',
};

function EmptyFieldValue({ isPending, renderWhenPending = defaultPendingProps, renderWhenEmpty }: EmptyStateRenderer & { isPending: boolean }) {
  if (isPending) {
    return <Skeleton {...renderWhenPending} />;
  }

  if (renderWhenEmpty === null) {
    return null;
  }

  return renderWhenEmpty ?? DEFAULT_EMPTY_VALUE;
}

export function getListItemHasValue(value: ReadonlyListItemValueType) {
  return match(value)
    .with({ string: P.not(P.nullish) }, (v) => !!v.string.value)
    .with({ number: P.not(P.nullish) }, (v) => v.number.value !== undefined && v.number.value !== '')
    .with({ currency: P.not(P.nullish) }, (v) => v.currency.value !== undefined && v.currency.value !== '')
    .with({ asset: P.not(P.nullish) }, (v) => v.asset.amount !== undefined && v.asset.amount !== '')
    .with({ date: P.not(P.nullish) }, (v) => !!v.date.value)
    .with({ id: P.not(P.nullish) }, (v) => !!v.id.value)
    .with({ sensitiveString: P.not(P.nullish) }, (v) => v.sensitiveString.value !== undefined && v.sensitiveString.value !== '')
    .with({ yesOrNo: P.not(P.nullish) }, (v) => v.yesOrNo.value !== undefined)
    .with({ node: P.not(P.nullish) }, (v) => v.node.value !== null && v.node.value !== undefined)
    .otherwise(() => false);
}

type ReadonlyListItemValueTypeProps = {
  defaultLinkElement?: LinkElementType;
  defaultRenderWhenEmpty?: ReactNode;
  isPending?: boolean;
  typography?: TypographyProps;
} & ReadonlyListItemValueType;

export function ReadonlyListItemValue({
  defaultLinkElement = 'a',
  defaultRenderWhenEmpty = null,
  isPending = false,
  ...value
}: ReadonlyListItemValueTypeProps) {
  const { typography: providedTypography, ...restOfValue } = value;
  const typography = match(value)
    .with(
      P.union({ string: { to: P.not(P.nullish) } }, { id: { to: P.not(P.nullish) } }),
      () =>
        ({
          ...(providedTypography ?? defaultTextLinkValueTypographyProps),
          tone: 'md',
        }) as const satisfies TypographyProps,
    )
    .otherwise(() => providedTypography ?? defaultValueTypographyProps);

  return (
    <TypographyProvider value={typography}>
      {match(restOfValue)
        .with({ string: P.not(P.nullish) }, (v) => {
          const { className, renderWhenEmpty, renderWhenPending, value: stringValue, to, ...rest } = v.string;

          if (!getListItemHasValue(v)) {
            return (
              <Typography className={className} {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={renderWhenPending}
                />
              </Typography>
            );
          }

          if (to) {
            return (
              <TextLink className={className} LinkElement={defaultLinkElement} preload={false} {...rest} to={to} typography={typography}>
                {stringValue}
              </TextLink>
            );
          }

          return (
            <Typography className={className} {...typography}>
              {stringValue}
            </Typography>
          );
        })
        .with({ number: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v) && !v.number.renderWhenEmpty) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <NumberFormat {...v.number} typography={typography} />;
        })
        .with({ currency: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v) && !v.currency.renderWhenEmpty) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <CurrencyFormat {...v.currency} typography={typography} />;
        })
        .with({ asset: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v) && !v.asset.renderWhenEmpty) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <AssetFormat {...v.asset} typography={typography} />;
        })
        .with({ date: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v)) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <DateFormat {...v.date} typography={typography} />;
        })
        .with({ id: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v)) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <ID gap={2} LinkElement={defaultLinkElement} preload={false} {...v.id} typographyProps={typography} />;
        })
        .with({ sensitiveString: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v)) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <SensitiveValueFormat {...v.sensitiveString} valueTypographyProps={typography} />;
        })
        .with({ yesOrNo: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v)) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <Typography {...typography}>{v.yesOrNo.value ? v.yesOrNo.trueLabel || 'Yes' : v.yesOrNo.falseLabel || 'No'}</Typography>;
        })
        .with({ node: P.not(P.nullish) }, (v) => {
          if (!getListItemHasValue(v)) {
            return (
              <Typography {...typography}>
                <EmptyFieldValue
                  isPending={isPending}
                  renderWhenEmpty={v.renderWhenEmpty ?? defaultRenderWhenEmpty}
                  renderWhenPending={v.renderWhenPending}
                />
              </Typography>
            );
          }

          return <Typography {...typography}>{v.node.value}</Typography>;
        })
        .otherwise(() => (
          <Typography {...typography}>
            <EmptyFieldValue isPending={isPending} renderWhenEmpty={defaultRenderWhenEmpty} />
          </Typography>
        ))}
    </TypographyProvider>
  );
}
